import { TextField } from '@mui/material';
import {
  AutocompleteAdapter,
  IdentityField,
  MultipleAutocompleteAdapter,
  TextFieldAdapter
} from 'components/enhanced';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { SectionHeader } from 'components/ui/typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  categoryService,
  examTagService,
  regionalRateService,
  workstationService
} from 'shared/services';
import { Option } from 'shared/types/general';
import { IRegionalRate } from '../../regional-rates/types';
import { IExam } from '../types';

interface ExamFormParams {
  exam: IExam;
  onSubmit: any;
  disableEdit?: boolean;
}

function ExamForm({ onSubmit, exam, disableEdit }: ExamFormParams) {
  const { t } = useTranslation();

  const [workstations, setWorkstations] = useState<Option[]>([]);
  const [examTags, setExamTags] = useState<Option[]>([]);
  const [categories, setCategories] = useState<Option[]>([]);
  const [regionalRates, setRegionalRates] = useState<IRegionalRate[]>([]);
  const [regionalRate, setRegionalRate] = useState<IRegionalRate | null>(null);

  useEffect(() => {
    workstationService.getAllBaseInformation().then(setWorkstations);
    examTagService.getAllPaginated().then(setExamTags);
    regionalRateService.getAllBaseInformation().then(setRegionalRates);
    categoryService.getAllBaseInformation().then(setCategories);
  }, []);

  useEffect(() => {
    if (exam?.regional_rate)
      setRegionalRate(regionalRates.find((s) => s.id === exam.regional_rate));
    else setRegionalRate(null);
  }, [exam]);

  const regionalRateField = (field: string) => {
    if (regionalRates) {
      if (regionalRate) {
        const rate = regionalRates.find((s) => s.id === regionalRate.id);
        return rate ? rate[field] : '';
      }
      if (exam?.regional_rate) {
        const rate = regionalRates.find((s) => s.id === exam.regional_rate);
        return rate ? rate[field] : '';
      }
    }
    return '';
  };

  return (
    <>
      {exam && (
        <EnhancedForm
          edit={true}
          onSubmit={(values) => {
            if (!disableEdit) {
              onSubmit({ ...values });
            }
          }}
          disableEdit={disableEdit}
          autoStyleLayout={true}
          columns={10}>
          <SectionHeader title={t('global.base-info')} gridSpan />
          <IdentityField
            gridSpan={10}
            initialValue={exam.code}
            name="code"
            component={TextFieldAdapter}
            label={t('global.code')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={10}
            initialValue={exam.description}
            name="description"
            component={TextFieldAdapter}
            label={t('global.description')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={10}
            name="category"
            label={t('entities.exam.category')}
            component={AutocompleteAdapter}
            initValue={categories.find((s) => s.id === exam.category)}
            defaultValue={exam.category}
            options={categories}
            type="text"
            disabled={disableEdit}
          />
          {workstations && (
            <IdentityField
              gridSpan={10}
              name="workstations"
              component={MultipleAutocompleteAdapter}
              options={workstations}
              initValue={exam.workstations}
              label={t('global.workstations')}
              type="text"
              disabled={disableEdit}
            />
          )}
          <IdentityField
            gridSpan={5}
            initialValue={exam.contractual_rate}
            name="contractual_rate"
            component={TextFieldAdapter}
            label={t('entities.exam.contractual_rate')}
            type="number"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={5}
            name="tags"
            component={MultipleAutocompleteAdapter}
            freeSolo={true}
            options={examTags}
            optionValue={'code'}
            initValue={exam?.tags}
            label={t('entities.exam.tags')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={10}
            initialValue={exam.notes}
            name="notes"
            component={TextFieldAdapter}
            label={t('global.notes')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={10}
            name="code_loinc"
            label={t(`entities.exam.code_loinc`)}
            initialValue={exam.code_loinc}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />
          <SectionHeader title={t('global.regional_price_list')} gridSpan />
          {regionalRates && (
            <>
              <IdentityField
                gridSpan={5}
                name="regional_rate"
                label={t('entities.exam.regional_code')}
                component={AutocompleteAdapter}
                initValue={regionalRates.find((s) => s.id === exam.regional_rate)}
                defaultValue={exam.regional_rate}
                options={regionalRates}
                setValue={setRegionalRate}
                type="text"
                disabled={disableEdit}
              />
              <div style={{ gridColumn: 'span 5' }}>
                <TextField
                  value={regionalRateField('rate')}
                  label={t('entities.exam.regional_rate')}
                  type="number"
                  disabled
                  fullWidth
                />
              </div>
              <div style={{ gridColumn: 'span 10' }}>
                <TextField
                  value={regionalRateField('description')}
                  label={t('entities.exam.price_list_description')}
                  type="text"
                  disabled
                  fullWidth
                />
              </div>
            </>
          )}
          <SectionHeader title={t('global.other_fares')} gridSpan />
          {['rate_1', 'rate_2', 'rate_3', 'rate_4', 'rate_5'].map((field, idx) => (
            <div style={{ gridColumn: 'span 2' }} key={idx}>
              <IdentityField
                name={field}
                component={TextFieldAdapter}
                defaultValue={exam && exam[field]}
                label={t(`entities.exam.${field}`)}
                type="number"
                disabled={disableEdit}
              />
            </div>
          ))}
          <SectionHeader title={'Extra'} gridSpan />
          {['extra_1', 'extra_2', 'extra_3', 'extra_4', 'extra_5'].map((field, idx) => (
            <div style={{ gridColumn: 'span 2' }} key={idx}>
              <IdentityField
                name={field}
                component={TextFieldAdapter}
                defaultValue={exam && exam[field]}
                label={t(`entities.exam.${field}`)}
                type="text"
                disabled={disableEdit}
              />
            </div>
          ))}
        </EnhancedForm>
      )}
    </>
  );
}

export default ExamForm;
